/**
 * Local Database - Report Generation
 *
 * Functions for generating financial reports from local data.
 */

import { findAncestorAtDepth, todayString } from './report-helpers'
import { localStore, calculateAccountBalances } from './store'

import type {
  LocalAccount,
  LocalTrialBalanceReport,
  LocalTrialBalanceRow,
  LocalIncomeStatementReport,
  LocalIncomeStatementRow,
  LocalBalanceSheetReport,
  LocalBalanceSheetRow,
  LocalGeneralLedgerAccount,
  LocalGeneralLedgerEntry,
} from './types'
import type { AccountType } from '../../constants/labels'

// ============================================================================
// Trial Balance helpers
// ============================================================================

interface DebitCredit { debitBalance: number; creditBalance: number }

function trialBalanceAccountBalance(
  accountId: string,
  normalBalance: string,
  balances: Map<string, { debit: number; credit: number }>
): DebitCredit {
  const bal = balances.get(accountId) ?? { debit: 0, credit: 0 }
  const netDebit = bal.debit - bal.credit
  const netCredit = bal.credit - bal.debit
  let debitBalance = 0
  let creditBalance = 0
  if (normalBalance === 'NORMAL_BALANCE_DEBIT') {
    if (netDebit > 0) { debitBalance = netDebit }
    /* v8 ignore next */
    else if (netDebit < 0) { creditBalance = -netDebit }
  } else {
    if (netCredit > 0) { creditBalance = netCredit }
    /* v8 ignore next */
    else if (netCredit < 0) { debitBalance = -netCredit }
  }
  return { debitBalance, creditBalance }
}

function buildTrialBalanceRowsAllDepths(
  accounts: LocalAccount[],
  balances: Map<string, { debit: number; credit: number }>,
  shouldIncludeZeroBalances: boolean
): { rows: LocalTrialBalanceRow[]; totalDebit: number; totalCredit: number } {
  const rows: LocalTrialBalanceRow[] = []
  let totalDebit = 0
  let totalCredit = 0
  for (const account of accounts.sort((a, b) => a.code.localeCompare(b.code))) {
    const { debitBalance, creditBalance } = trialBalanceAccountBalance(account.id, account.normalBalance, balances)
    if (!shouldIncludeZeroBalances && debitBalance === 0 && creditBalance === 0) { continue }
    rows.push({ accountId: account.id, accountCode: account.code, accountName: account.name, accountType: account.accountType, debitBalance, creditBalance })
    totalDebit += debitBalance
    totalCredit += creditBalance
  }
  return { rows, totalDebit, totalCredit }
}

function buildTrialBalanceRowsAggregated(
  accounts: LocalAccount[],
  allAccounts: LocalAccount[],
  balances: Map<string, { debit: number; credit: number }>,
  maxDepth: number,
  shouldIncludeZeroBalances: boolean
): { rows: LocalTrialBalanceRow[]; totalDebit: number; totalCredit: number } {
  const aggregatedBalances = new Map<string, { account: LocalAccount; debitBalance: number; creditBalance: number }>()
  let totalDebit = 0
  let totalCredit = 0
  for (const account of accounts) {
    const { debitBalance, creditBalance } = trialBalanceAccountBalance(account.id, account.normalBalance, balances)
    if (debitBalance === 0 && creditBalance === 0) { continue }
    totalDebit += debitBalance
    totalCredit += creditBalance
    const targetAccount = findAncestorAtDepth(account, maxDepth, allAccounts)
    if (targetAccount) {
      const existing = aggregatedBalances.get(targetAccount.id)
      if (existing) {
        existing.debitBalance += debitBalance
        existing.creditBalance += creditBalance
      } else {
        aggregatedBalances.set(targetAccount.id, { account: targetAccount, debitBalance, creditBalance })
      }
    }
  }
  const rows = Array.from(aggregatedBalances.values())
    .filter(({ debitBalance, creditBalance }) => shouldIncludeZeroBalances || debitBalance !== 0 || creditBalance !== 0)
    .sort((a, b) => a.account.code.localeCompare(b.account.code))
    .map(({ account, debitBalance, creditBalance }) => ({
      accountId: account.id, accountCode: account.code, accountName: account.name,
      accountType: account.accountType, debitBalance, creditBalance,
    }))
  return { rows, totalDebit, totalCredit }
}

// ============================================================================
// Trial Balance
// ============================================================================

/**
 * Generate Trial Balance report from local data
 * @param options.maxDepth - Maximum account depth to show (1 = top-level only, 2 = include sub-accounts, null = show all)
 */
export function getLocalTrialBalance(
  orgId: string,
  options?: { asOfDate?: string; shouldIncludeZeroBalances?: boolean; ledgerId?: string; maxDepth?: number | null }
): LocalTrialBalanceReport {
  const asOfDate = options?.asOfDate ?? todayString()
  const ledgerId = options?.ledgerId ?? localStore.defaultLedgerId
  const shouldIncludeZeroBalances = options?.shouldIncludeZeroBalances ?? false
  const maxDepth = options?.maxDepth === undefined ? 1 : options.maxDepth

  const balances = calculateAccountBalances(orgId, ledgerId, asOfDate)
  const allAccounts = Array.from(localStore.accounts.values())
    .filter(a => a.orgId === orgId && (!ledgerId || a.ledgerId === ledgerId))
  const accounts = allAccounts.filter(a => !a.isHeader)

  const { rows, totalDebit, totalCredit } = maxDepth === null
    ? buildTrialBalanceRowsAllDepths(accounts, balances, shouldIncludeZeroBalances)
    : buildTrialBalanceRowsAggregated(accounts, allAccounts, balances, maxDepth, shouldIncludeZeroBalances)

  return {
    asOfDate,
    generatedAt: new Date().toISOString(),
    rows,
    totalDebit,
    totalCredit,
    isBalanced: Math.abs(totalDebit - totalCredit) < 1,
  }
}

// ============================================================================
// Income Statement helpers
// ============================================================================

function buildIncomeStatementSectionAllDepths(
  sectionAccounts: LocalAccount[],
  accountBalances: Map<string, number>,
  isExpense: boolean
): { rows: LocalIncomeStatementRow[]; total: number } {
  const rows: LocalIncomeStatementRow[] = []
  let total = 0
  for (const account of sectionAccounts.sort((a, b) => a.code.localeCompare(b.code))) {
    const balance = accountBalances.get(account.id) ?? 0
    const amount = isExpense ? -balance : balance
    if (amount !== 0) {
      rows.push({ accountId: account.id, accountCode: account.code, accountName: account.name, amount })
      total += amount
    }
  }
  return { rows, total }
}

function buildIncomeStatementSectionAggregated(
  sectionAccounts: LocalAccount[],
  allAccounts: LocalAccount[],
  accountBalances: Map<string, number>,
  isExpense: boolean,
  maxDepth: number
): { rows: LocalIncomeStatementRow[]; total: number } {
  const aggregatedBalances = new Map<string, { account: LocalAccount; balance: number }>()
  let total = 0
  for (const account of sectionAccounts) {
    const balance = accountBalances.get(account.id) ?? 0
    const amount = isExpense ? -balance : balance
    if (amount === 0) { continue }
    total += amount
    const targetAccount = findAncestorAtDepth(account, maxDepth, allAccounts)
    if (targetAccount) {
      const existing = aggregatedBalances.get(targetAccount.id)
      if (existing) { existing.balance += amount }
      else { aggregatedBalances.set(targetAccount.id, { account: targetAccount, balance: amount }) }
    }
  }
  const rows = Array.from(aggregatedBalances.values())
    .filter(({ balance }) => balance !== 0)
    .sort((a, b) => a.account.code.localeCompare(b.account.code))
    .map(({ account, balance }) => ({
      accountId: account.id, accountCode: account.code, accountName: account.name, amount: balance,
    }))
  return { rows, total }
}

// eslint-disable-next-line max-params -- TODO(refactor): convert to options object
function buildIncomeStatementSection(
  accountType: AccountType,
  isExpense: boolean,
  accounts: LocalAccount[],
  allAccounts: LocalAccount[],
  accountBalances: Map<string, number>,
  maxDepth: number | null
): { rows: LocalIncomeStatementRow[]; total: number } {
  const sectionAccounts = accounts.filter(a => a.accountType === accountType)
  if (maxDepth === null) {
    return buildIncomeStatementSectionAllDepths(sectionAccounts, accountBalances, isExpense)
  }
  return buildIncomeStatementSectionAggregated(sectionAccounts, allAccounts, accountBalances, isExpense, maxDepth)
}

// ============================================================================
// Income Statement
// ============================================================================

/**
 * Generate Income Statement report from local data
 * @param options.maxDepth - Maximum account depth to show (1 = top-level only, 2 = include sub-accounts, null = show all)
 */
export function getLocalIncomeStatement(
  orgId: string,
  options: { startDate: string; endDate: string; ledgerId?: string; maxDepth?: number | null }
): LocalIncomeStatementReport {
  const { startDate, endDate } = options
  const ledgerId = options.ledgerId ?? localStore.defaultLedgerId
  const maxDepth = options.maxDepth === undefined ? 1 : options.maxDepth

  const entries = Array.from(localStore.journalEntries.values())
    .filter(e => {
      /* v8 ignore next 3 */
      if (e.orgId !== orgId) { return false }
      if (ledgerId && e.ledgerId !== ledgerId) { return false }
      if (e.status !== 'posted') { return false }
      if (e.entryDate < startDate || e.entryDate > endDate) { return false }
      return true
    })

  const accountBalances = new Map<string, number>()
  for (const entry of entries) {
    for (const line of entry.lines) {
      const current = accountBalances.get(line.accountId) ?? 0
      accountBalances.set(line.accountId, current + line.creditAmount - line.debitAmount)
    }
  }

  const allAccounts = Array.from(localStore.accounts.values())
    .filter(a => a.orgId === orgId && (!ledgerId || a.ledgerId === ledgerId))
  const accounts = allAccounts.filter(a => !a.isHeader)

  const { rows: revenueRows, total: totalRevenue } =
    buildIncomeStatementSection('ACCOUNT_TYPE_REVENUE', false, accounts, allAccounts, accountBalances, maxDepth)
  const { rows: expenseRows, total: totalExpenses } =
    buildIncomeStatementSection('ACCOUNT_TYPE_EXPENSE', true, accounts, allAccounts, accountBalances, maxDepth)

  const netIncome = totalRevenue - totalExpenses
  return {
    periodStart: startDate,
    periodEnd: endDate,
    generatedAt: new Date().toISOString(),
    sections: [
      { sectionType: 'REVENUE', sectionName: 'Revenue', rows: revenueRows, sectionTotal: totalRevenue },
      { sectionType: 'EXPENSE', sectionName: 'Expenses', rows: expenseRows, sectionTotal: totalExpenses },
    ],
    totalRevenue,
    totalExpenses,
    netIncome: Math.abs(netIncome),
    isProfit: netIncome >= 0,
  }
}

// ============================================================================
// Balance Sheet helpers
// ============================================================================

function getNetBalance(
  accountId: string,
  normalBalance: string,
  balances: Map<string, { debit: number; credit: number }>
): number {
  const bal = balances.get(accountId) ?? { debit: 0, credit: 0 }
  if (normalBalance === 'NORMAL_BALANCE_DEBIT') { return bal.debit - bal.credit }
  return bal.credit - bal.debit
}

function buildBalanceSheetSectionAllDepths(
  sectionAccounts: LocalAccount[],
  balances: Map<string, { debit: number; credit: number }>
): { rows: LocalBalanceSheetRow[]; total: number } {
  const rows: LocalBalanceSheetRow[] = []
  let total = 0
  for (const account of sectionAccounts.sort((a, b) => a.code.localeCompare(b.code))) {
    const balance = getNetBalance(account.id, account.normalBalance, balances)
    if (balance !== 0) {
      rows.push({ accountId: account.id, accountCode: account.code, accountName: account.name, balance })
      total += balance
    }
  }
  return { rows, total }
}

function buildBalanceSheetSectionAggregated(
  sectionAccounts: LocalAccount[],
  allAccounts: LocalAccount[],
  balances: Map<string, { debit: number; credit: number }>,
  maxDepth: number
): { rows: LocalBalanceSheetRow[]; total: number } {
  const aggregatedBalances = new Map<string, { account: LocalAccount; balance: number }>()
  let total = 0
  for (const account of sectionAccounts) {
    const balance = getNetBalance(account.id, account.normalBalance, balances)
    if (balance === 0) { continue }
    total += balance
    const targetAccount = findAncestorAtDepth(account, maxDepth, allAccounts)
    if (targetAccount) {
      const existing = aggregatedBalances.get(targetAccount.id)
      if (existing) { existing.balance += balance }
      else { aggregatedBalances.set(targetAccount.id, { account: targetAccount, balance }) }
    }
  }
  const rows = Array.from(aggregatedBalances.values())
    .filter(({ balance }) => balance !== 0)
    .sort((a, b) => a.account.code.localeCompare(b.account.code))
    .map(({ account, balance }) => ({
      accountId: account.id, accountCode: account.code, accountName: account.name, balance,
    }))
  return { rows, total }
}

function buildBalanceSheetSection(
  accountType: AccountType,
  allAccounts: LocalAccount[],
  balances: Map<string, { debit: number; credit: number }>,
  maxDepth: number | null
): { rows: LocalBalanceSheetRow[]; total: number } {
  const sectionAccounts = allAccounts.filter(a => a.accountType === accountType && !a.isHeader)
  if (maxDepth === null) {
    return buildBalanceSheetSectionAllDepths(sectionAccounts, balances)
  }
  return buildBalanceSheetSectionAggregated(sectionAccounts, allAccounts, balances, maxDepth)
}

// ============================================================================
// Balance Sheet
// ============================================================================

/**
 * Generate Balance Sheet report from local data
 * @param options.maxDepth - Maximum account depth to show (1 = top-level only, 2 = include sub-accounts, null = show all)
 */
export function getLocalBalanceSheet(
  orgId: string,
  options?: { asOfDate?: string; ledgerId?: string; maxDepth?: number | null }
): LocalBalanceSheetReport {
  const asOfDate = options?.asOfDate ?? todayString()
  const ledgerId = options?.ledgerId ?? localStore.defaultLedgerId
  const maxDepth = options?.maxDepth === undefined ? 1 : options.maxDepth

  const balances = calculateAccountBalances(orgId, ledgerId, asOfDate)
  const allAccounts = Array.from(localStore.accounts.values())
    .filter(a => a.orgId === orgId && (!ledgerId || a.ledgerId === ledgerId))

  const { rows: assetRows, total: totalAssets } = buildBalanceSheetSection('ACCOUNT_TYPE_ASSET', allAccounts, balances, maxDepth)
  const { rows: liabilityRows, total: totalLiabilities } = buildBalanceSheetSection('ACCOUNT_TYPE_LIABILITY', allAccounts, balances, maxDepth)
  const { rows: equityRows, total: baseEquityTotal } = buildBalanceSheetSection('ACCOUNT_TYPE_EQUITY', allAccounts, balances, maxDepth)
  const { total: totalRevenue } = buildBalanceSheetSection('ACCOUNT_TYPE_REVENUE', allAccounts, balances, maxDepth)
  const { total: totalExpenses } = buildBalanceSheetSection('ACCOUNT_TYPE_EXPENSE', allAccounts, balances, maxDepth)

  const netIncome = totalRevenue - totalExpenses
  let totalEquity = baseEquityTotal
  if (netIncome !== 0) {
    equityRows.push({
      accountId: 'net-income', accountCode: '',
      accountName: netIncome >= 0 ? 'Net Income (Current Period)' : 'Net Loss (Current Period)',
      balance: netIncome,
    })
    totalEquity += netIncome
  }

  const totalLiabilitiesAndEquity = totalLiabilities + totalEquity
  return {
    asOfDate,
    generatedAt: new Date().toISOString(),
    assets: { sectionName: 'Assets', categories: [{ categoryName: 'Current Assets', rows: assetRows, categoryTotal: totalAssets }], sectionTotal: totalAssets },
    liabilities: { sectionName: 'Liabilities', categories: [{ categoryName: 'Current Liabilities', rows: liabilityRows, categoryTotal: totalLiabilities }], sectionTotal: totalLiabilities },
    equity: { sectionName: 'Equity', categories: [{ categoryName: "Owner's Equity", rows: equityRows, categoryTotal: totalEquity }], sectionTotal: totalEquity },
    totalAssets,
    totalLiabilitiesAndEquity,
    isBalanced: Math.abs(totalAssets - totalLiabilitiesAndEquity) < 1,
  }
}

// ============================================================================
// General Ledger helpers
// ============================================================================

type GLLine = { debitAmount: number; creditAmount: number; memo?: string; accountId: string }
type GLEntry = { id: string; entryDate: string; entryNumber: string; memo?: string; reference?: string; lines: GLLine[] }

function lineContribution(line: GLLine, isDebitNormal: boolean): number {
  return isDebitNormal
    ? line.debitAmount - line.creditAmount
    : line.creditAmount - line.debitAmount
}

function computeOpeningAndTotals(
  account: LocalAccount,
  allEntries: GLEntry[],
  startDate: string,
  endDate: string,
): { openingBalance: number; periodDebit: number; periodCredit: number } {
  const isDebitNormal = account.normalBalance === 'NORMAL_BALANCE_DEBIT'
  let openingBalance = 0
  let periodDebit = 0
  let periodCredit = 0
  for (const entry of allEntries) {
    const lines = entry.lines.filter(l => l.accountId === account.id)
    for (const line of lines) {
      if (entry.entryDate < startDate) {
        openingBalance += lineContribution(line, isDebitNormal)
      } else if (entry.entryDate <= endDate) {
        periodDebit += line.debitAmount
        periodCredit += line.creditAmount
      }
    }
  }
  return { openingBalance, periodDebit, periodCredit }
}

function buildPeriodEntries(
  account: LocalAccount,
  allEntries: GLEntry[],
  startDate: string,
  endDate: string,
  openingBalance: number,
): LocalGeneralLedgerEntry[] {
  const isDebitNormal = account.normalBalance === 'NORMAL_BALANCE_DEBIT'
  const periodEntries: LocalGeneralLedgerEntry[] = []
  let runningBalance = openingBalance
  for (const entry of allEntries) {
    if (entry.entryDate < startDate || entry.entryDate > endDate) { continue }
    const lines = entry.lines.filter(l => l.accountId === account.id)
    for (const line of lines) {
      runningBalance += lineContribution(line, isDebitNormal)
      periodEntries.push({
        entryDate: entry.entryDate,
        entryNumber: entry.entryNumber,
        journalEntryId: entry.id,
        /* v8 ignore next 2 */
        memo: line.memo ?? entry.memo ?? '',
        reference: entry.reference ?? '',
        debit: line.debitAmount,
        credit: line.creditAmount,
        balance: runningBalance,
      })
    }
  }
  return periodEntries
}

function computeGeneralLedgerAccount(
  account: LocalAccount,
  allEntries: ReturnType<typeof Array.from<import('./types').LocalJournalEntry>>,
  startDate: string,
  endDate: string
): { openingBalance: number; periodDebit: number; periodCredit: number; periodEntries: LocalGeneralLedgerEntry[] } {
  const totals = computeOpeningAndTotals(account, allEntries, startDate, endDate)
  const periodEntries = buildPeriodEntries(account, allEntries, startDate, endDate, totals.openingBalance)
  return { ...totals, periodEntries }
}

// ============================================================================
// General Ledger
// ============================================================================

/**
 * Generate General Ledger report from local data
 */
export function getLocalGeneralLedger(
  orgId: string,
  options: { startDate: string; endDate: string; accountId?: string; ledgerId?: string }
): LocalGeneralLedgerAccount[] {
  const { startDate, endDate, accountId } = options
  const ledgerId = options.ledgerId ?? localStore.defaultLedgerId

  let accounts = Array.from(localStore.accounts.values())
    .filter(a => a.orgId === orgId && (!ledgerId || a.ledgerId === ledgerId) && !a.isHeader)
    .sort((a, b) => a.code.localeCompare(b.code))

  if (accountId) { accounts = accounts.filter(a => a.id === accountId) }

  const allEntries = Array.from(localStore.journalEntries.values())
    .filter(e => {
      /* v8 ignore next */
      if (e.orgId !== orgId) { return false }
      /* v8 ignore next */
      if (ledgerId && e.ledgerId !== ledgerId) { return false }
      /* v8 ignore next */
      if (e.status !== 'posted') { return false }
      return true
    })
    .sort((a, b) => a.entryDate.localeCompare(b.entryDate))

  const result: LocalGeneralLedgerAccount[] = []
  for (const account of accounts) {
    const { openingBalance, periodDebit, periodCredit, periodEntries } =
      computeGeneralLedgerAccount(account, allEntries, startDate, endDate)

    if (openingBalance !== 0 || periodEntries.length > 0) {
      result.push({
        accountId: account.id,
        accountCode: account.code,
        accountName: account.name,
        openingBalance,
        periodDebit,
        periodCredit,
        closingBalance: openingBalance + (account.normalBalance === 'NORMAL_BALANCE_DEBIT'
          ? periodDebit - periodCredit
          : periodCredit - periodDebit),
        entries: periodEntries,
      })
    }
  }

  return result
}
